import { FieldError, useFormContext } from 'react-hook-form';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
import { useEffect } from 'react';

import { InputField } from 'components/v2/input/InputField';
import { useLocalization } from 'localization';
import ContinueAndPreviewContainer from 'components/v2/container/ContinueAndPreviewContainer';
import { ArrowRightIcon } from 'components/v2/icons/ArrowRightIcon';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import { InputSelectField } from 'components/v2/input/InputSelectField';
import { LAYOUT_SPACING } from 'constants/ui/style';
import { TextAreaInputField } from 'components/v2/input/TextAreaInputField';
import { CheckboxField } from 'components/v2/checkbox/CheckboxField';
import CreateNewOrderEditAddress, { initialEditAddressValue } from './CreateOrderEditAddress';
import CreateNewOrderContactPerson from './CreateOrderContactPerson';
import { NumberInputField } from 'components/v2/input/NumberInputField';
import CreateOrderWatchers from './CreateOrderWatchers';
import { ORDER_CREATION_PACKAGE_TYPE } from 'constants/ui/status';
import GoogleMapViewer from 'components/v2/maps/GoogleMapViewer';
import { colors } from 'theme/mui/colors';
import { getCoordinate } from 'helpers/getCoordinate';
import InputPhoneNumber from 'components/v2/input/InputPhoneNumber';
import notifyHandler from 'utils/notifyHandler';
import { useUsersBranchById } from 'modules/branches/hooks';
import Show from 'components/v2/show/Show';
import WarnPropertySizeModal from 'modules/v2/servicesmodal/order/WarnPropertySizeModal';
import { createNewOrderStore } from 'stores/createNewOrderStore';
import CollapsibleSection from 'components/v2/accordion/CollapsibleSection';
import CreateOrderKeyPickUpAddress from './CreateOrderKeyPickUpAddress';
import { useGetPickUpKeyAddressApi } from 'api/address/query';
import { mapAddressKeyPickUp } from 'api/address/helpers';
import { getPropertyTypesApi } from 'api/orders/query';
import { PropertyTypes } from 'constants/enums/propertyTypes';

const CreateOrderStep1 = () => {
  const { t } = useLocalization();

  const { nextStep, setFormData, formData, discardFlow } = createNewOrderStore();
  const isPackageTypeProperty = formData?.packageType === ORDER_CREATION_PACKAGE_TYPE.property;
  const propertyTypesWithBasementWarning = new Set([
    PropertyTypes.TWO_FAMILY_DETACHED_HOUSE,
    PropertyTypes.COMMERCIAL_WITH_RESIDENCE,
    PropertyTypes.COMMERCIAL_AND_PRIVATE,
    PropertyTypes.RESIDENTIAL_PROPERTY,
    PropertyTypes.TERRACED_HOUSE,
    PropertyTypes.DETACHED_HOUSE,
    PropertyTypes.SHARED_OWNERSHIP_DETACHED_HOUSE,
  ]);
  const {
    control,
    trigger,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();

  const foundBranch = useUsersBranchById(formData.branchId);
  const showEndUserInvoice = !!foundBranch?.endUserInvoicing;
  const showBusinessInvoice = !!foundBranch?.businessInvoicing;

  const { data: propertyTypeOptions, isLoading } = getPropertyTypesApi();
  let transformedPropertyTypeOptions = propertyTypeOptions ?? [];
  const keyPickup = watch('keyPickup');
  const shouldSendEndUserInvoiceValue = watch('shouldSendEndUserInvoice');
  const shouldSendElectronicInvoiceValue = watch('shouldSendElectronicInvoice');
  const isPropertyTypeNotExistInTypeOptions = !(
    Boolean(getValues('property.type')) &&
    propertyTypeOptions?.some(option => option.value === getValues('property.type'))
  );
  const { toggleModal, isModalOpen } = MODALS_STORE.createOrder();
  const { data } = useGetPickUpKeyAddressApi(formData?.branchId, keyPickup);

  // this is to filter address that is not having 'text' property
  const filterValidAddress = data?.items?.filter(address => address.text);
  const mappedKeyPickUpAddress = mapAddressKeyPickUp(filterValidAddress?.[0]!);

  const onNext = async () => {
    const validationFields = [
      'address.full',
      'address.street',
      'address.houseNumber',
      'address.postNumber',
      'address.city',
      'shouldSendEndUserInvoice',
      'pickUpKeyAddress',
      'endUserInvoice',
      'shouldSendElectronicInvoice',
      'electronicInvoice',
      'contactPersons',
      'responsibleEmail',
      ...(isPackageTypeProperty ? ['property'] : []),
    ];
    const propertyData = getValues('property');
    const packageType = getValues('packageType');

    const livingArea = propertyData?.livingArea;
    const basementArea = propertyData?.basementArea;
    const shouldWarnForPropertyType = propertyTypesWithBasementWarning.has(propertyData?.type);
    const isWarnUser =
      (!livingArea || !basementArea) &&
      packageType === ORDER_CREATION_PACKAGE_TYPE.property &&
      shouldWarnForPropertyType;

    const isValid = await trigger(validationFields);
    if (isValid) {
      const currentFormValues = getValues();
      setFormData({
        ...formData,
        ...currentFormValues,
      });
      if (isWarnUser) {
        toggleModal(MODAL_ID.CREATE_ORDER_WARN_PROPERTY_SIZE);
      } else {
        nextStep();
      }
    } else {
      notifyHandler({ content: t('common.messages.someFormFieldsAreInvalidPleaseCheck'), type: 'warning' });
    }
  };

  if (isPropertyTypeNotExistInTypeOptions) {
    transformedPropertyTypeOptions = [
      ...transformedPropertyTypeOptions,
      { label: getValues('property.type'), value: getValues('property.type') },
    ];
  }

  useEffect(() => {
    //Set longitude and latitude based on the full address
    const addressFull = formData?.address?.full ?? watch('address.full');

    const fetchCoordinates = async () => {
      const coordinates = await getCoordinate(addressFull);
      setValue('longitude', coordinates?.longitude);
      setValue('latitude', coordinates?.latitude);
    };

    if (addressFull) {
      fetchCoordinates();
    }

    if (data) setValue('pickUpKeyAddress', mappedKeyPickUpAddress);
  }, [watch('address.full'), formData?.address?.full, data]);

  useEffect(() => {
    if (!shouldSendEndUserInvoiceValue) return;

    const endUserAddress = getValues('endUserInvoice.address');
    const address = getValues('address');
    const hasNoEndUserAddress = !endUserAddress?.full;
    const hasAddress = !!address?.full;

    if (hasNoEndUserAddress && hasAddress) {
      setValue('endUserInvoice.address', {
        ...address,
      });
    }

    const contactPersons = getValues('contactPersons') ?? [];
    const owner = contactPersons.find((person: { type?: string }) => person?.type?.toLowerCase() === 'owner');
    if (owner) {
      const currentEndUserInvoice = getValues('endUserInvoice');
      if (owner.name && !currentEndUserInvoice?.name) setValue('endUserInvoice.name', owner.name);
      if (owner.email && !currentEndUserInvoice?.email) setValue('endUserInvoice.email', owner.email);
      if (owner.phone && !currentEndUserInvoice?.phone) setValue('endUserInvoice.phone', owner.phone);
    }
  }, [shouldSendEndUserInvoiceValue]);

  return (
    <Stack direction="row" justifyContent="space-between" sx={{ height: '100%' }}>
      <Stack
        spacing={LAYOUT_SPACING.FORM.md}
        sx={{ width: '100%', padding: '1rem 1rem 1rem 0', overflowY: 'auto', marginBottom: '2rem' }}
      >
        {/*General information*/}
        <CollapsibleSection title={t('orders.pages.new.stepThree.generalInformationCardTitle')}>
          <Stack spacing={LAYOUT_SPACING.FORM.xs}>
            <Typography>
              {t('orders.pages.new.stepThree.caseInformationMessage')}:{' '}
              <Typography component="span" color={colors.primary.main}>
                {formData.case?.reference}
              </Typography>
            </Typography>

            <CreateNewOrderEditAddress
              modalId={MODAL_ID.CREATE_ORDER_GENERAL_INFORMATION_EDIT_ADDRESS}
              onAddAddress={value => {
                setValue('address', {
                  ...value,
                  full: value?.fullAddress,
                  floor: String(value?.floor),
                  houseNumber: String(value?.houseNumber),
                  appartmentNumber: String(value?.appartmentNumber),
                });
                setValue('longitude', value?.longitude);
                setValue('latitude', value?.latitude);
              }}
              error={(errors.address as any)?.full as FieldError}
              defaultValue={{
                fullAddress: watch('address.full'),
                street: watch('address.street'),
                houseNumber: watch('address.houseNumber'),
                postNumber: watch('address.postNumber'),
                city: watch('address.city'),
                area: watch('address.area'),
                appartmentNumber: watch('address.appartmentNumber'),
                floor: watch('address.floor'),
              }}
            />
            {isPackageTypeProperty && (
              <Grid2 container spacing={LAYOUT_SPACING.FORM.xs}>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <InputSelectField
                    name="property.type"
                    control={control}
                    options={transformedPropertyTypeOptions}
                    label="common.terms.propertyType"
                    menuHeight={300}
                    required
                    loading={isLoading}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <NumberInputField
                    control={control}
                    name="property.livingArea"
                    label="orders.pages.new.stepThree.livingAreaLabel"
                    required
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <NumberInputField
                    control={control}
                    name="property.basementArea"
                    label="orders.pages.new.stepThree.basementAreaLabel"
                    required
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <NumberInputField
                    control={control}
                    name="property.floors"
                    label="orders.pages.new.stepThree.numberOfFloorsLabel"
                    required
                  />
                </Grid2>
              </Grid2>
            )}
            <TextAreaInputField
              control={control}
              name="photographerComment"
              label="common.terms.photographerComment"
              placeholder="orders.pages.new.stepOne.photographerCommentDescription"
              rows={3}
            />
          </Stack>
        </CollapsibleSection>

        {/* Key pick up */}
        <Show when={isPackageTypeProperty}>
          <CollapsibleSection
            title={t('common.terms.keyPickup')}
            explanation={t('orders.pages.new.stepOne.keyPickUpExplanation')}
            defaultOpen={false}
          >
            <CheckboxField control={control} name="keyPickup" label="orders.pages.new.stepOne.keyShouldBePickedUp" />
            {keyPickup && (
              <CreateOrderKeyPickUpAddress
                modalId={MODAL_ID.CREATE_ORDER_GENERAL_INFORMATION_EDIT_ADDRESS_KEY_PICK_UP}
                onAddAddress={value => {
                  setValue('pickUpKeyAddress', {
                    ...value,
                    full: value?.fullAddress,
                  });
                }}
                defaultValue={{
                  ...initialEditAddressValue,
                  city: mappedKeyPickUpAddress.city ?? '',
                  postNumber: mappedKeyPickUpAddress.postNumber ?? '',
                  street: mappedKeyPickUpAddress.street ?? '',
                  houseNumber: mappedKeyPickUpAddress.houseNumber ?? (watch('address.houseNumber') || '0'),
                  fullAddress: mappedKeyPickUpAddress.full ?? '',
                  floor: Number(mappedKeyPickUpAddress.floor),
                  ...watch('pickUpKeyAddress'),
                  longitude: 0,
                  latitude: 0,
                }}
              />
            )}
          </CollapsibleSection>
        </Show>

        {/* End user invoice */}
        <Show when={showEndUserInvoice && !shouldSendElectronicInvoiceValue}>
          <CollapsibleSection
            title={t('orders.pages.details.endUserInvoicing')}
            defaultOpen={false}
            explanation={t('orders.pages.new.stepThree.endUserInvoiceDescription')}
          >
            <CheckboxField
              control={control}
              name="shouldSendEndUserInvoice"
              label="orders.pages.new.stepOne.generateEndUserInvoice"
              disabled={shouldSendElectronicInvoiceValue}
            />
            {shouldSendEndUserInvoiceValue && (
              <Grid2 container spacing={LAYOUT_SPACING.FORM.xs}>
                <Grid2 size={{ xs: 12 }}>
                  <CreateNewOrderEditAddress
                    modalId={MODAL_ID.CREATE_ORDER_GENERAL_INFORMATION_EDIT_ADDRESS_END_UER_INVOICE}
                    onAddAddress={value => {
                      setValue('endUserInvoice.address', {
                        ...value,
                        full: value?.fullAddress,
                      });
                    }}
                    error={(errors.endUserInvoice as any)?.address?.full as FieldError}
                    defaultValue={{
                      fullAddress: formData?.endUserInvoice?.address?.full,
                      appartmentNumber: formData?.endUserInvoice?.address?.appartmentNumber,
                      city: formData?.endUserInvoice?.address?.city,
                      floor: formData?.endUserInvoice?.address?.floor,
                      houseNumber: formData?.endUserInvoice?.address?.houseNumber,
                      postNumber: formData?.endUserInvoice?.address?.postNumber,
                      street: formData?.endUserInvoice?.address?.street,
                      area: formData?.endUserInvoice?.address?.area,
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 12, lg: 4 }}>
                  <InputField control={control} name="endUserInvoice.name" label="common.terms.name" required />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 12, lg: 4 }}>
                  <InputPhoneNumber name={'endUserInvoice.phone'} control={control} />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 12, lg: 4 }}>
                  <InputField control={control} name="endUserInvoice.email" label="common.terms.email" required />
                </Grid2>
              </Grid2>
            )}
          </CollapsibleSection>
        </Show>

        {/* Business invoice */}
        <Show when={showBusinessInvoice && !shouldSendEndUserInvoiceValue}>
          <CollapsibleSection
            title={t('orders.pages.new.stepThree.electronicInvoiceLabel')}
            defaultOpen={false}
            explanation={t('orders.pages.new.stepOne.businessInvoiceDescription')}
          >
            <CheckboxField
              control={control}
              name="shouldSendElectronicInvoice"
              label="orders.pages.new.stepOne.generateBusinessInvoice"
              disabled={shouldSendEndUserInvoiceValue}
            />
            {shouldSendElectronicInvoiceValue && (
              <Grid2 container spacing={LAYOUT_SPACING.FORM.xs}>
                <Grid2 size={{ xs: 12 }}>
                  <CreateNewOrderEditAddress
                    modalId={MODAL_ID.CREATE_ORDER_GENERAL_INFORMATION_EDIT_ADDRESS_BUSINESS_INVOICE}
                    onAddAddress={value => {
                      setValue('electronicInvoice.address', {
                        ...value,
                        full: value?.fullAddress,
                      });
                    }}
                    error={(errors.electronicInvoice as any)?.address?.full as FieldError}
                    defaultValue={{
                      fullAddress: formData?.electronicInvoice?.address?.full,
                      appartmentNumber: formData?.electronicInvoice?.address?.appartmentNumber,
                      city: formData?.electronicInvoice?.address?.city,
                      floor: formData?.electronicInvoice?.address?.floor,
                      houseNumber: formData?.electronicInvoice?.address?.houseNumber,
                      postNumber: formData?.electronicInvoice?.address?.postNumber,
                      street: formData?.electronicInvoice?.address?.street,
                      area: formData?.electronicInvoice?.address?.area,
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <InputField control={control} name="electronicInvoice.name" label="common.terms.name" required />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <InputField control={control} name="electronicInvoice.email" label="common.terms.email" required />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <InputPhoneNumber control={control} name={'electronicInvoice.phone'} />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <InputField control={control} name="electronicInvoice.cvr" label="common.terms.cvr" required />
                </Grid2>
                <Grid2 size={{ xs: 12, lg: 6 }}>
                  <InputField control={control} name="electronicInvoice.ean" label="common.terms.ean" required />
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                  <TextAreaInputField
                    control={control}
                    name="electronicInvoice.comment"
                    label="orders.pages.new.stepThree.electronicInvoiceCommentLabel"
                    placeholder="orders.pages.new.stepThree.electronicInvoiceCommentLabel"
                    required
                    rows={2}
                  />
                </Grid2>
              </Grid2>
            )}
          </CollapsibleSection>
        </Show>

        {/* Contact Person */}
        <CollapsibleSection title={t('common.terms.contactPerson')}>
          <CreateNewOrderContactPerson />
        </CollapsibleSection>

        {/* Watchers */}
        <CollapsibleSection title={t('orders.pages.new.stepThree.watchersCardTitle')}>
          <CreateOrderWatchers />
        </CollapsibleSection>
      </Stack>

      {/* Right side */}
      <ContinueAndPreviewContainer
        cancelElement={
          <Button onClick={discardFlow} size="large" variant="outlined" fullWidth>
            {t('common.actions.cancel')}
          </Button>
        }
        continueElement={
          <Button onClick={onNext} size="large" variant="contained" fullWidth endIcon={<ArrowRightIcon />}>
            {t('common.terms.continue')}
          </Button>
        }
        previewElement={
          <Box sx={{ width: '100%', height: '100%' }}>
            <GoogleMapViewer lng={watch('longitude')} lat={watch('latitude')} zoom={15} />
          </Box>
        }
      />
      {isModalOpen(MODAL_ID.CREATE_ORDER_WARN_PROPERTY_SIZE) && <WarnPropertySizeModal onConfirm={() => nextStep()} />}
    </Stack>
  );
};

export default CreateOrderStep1;
